<template>
  <div class="flex w-full gap-3 md:gap-4">
    <!-- Asset selection -->
    <MeteringPointPickerComponent
      v-if="!noMeteringPointMenu"
      ref="meteringPointPickerRef"
      v-model="filterStore.currentMeteringPoint"
      :allocation-points
      :assets
      :no-assets
      :no-allocation-points
      :with-portfolio
      :shaded-overlay="highlightPicker"
      :small="!mdAndLarger"
      class="flex-1 md:flex-none"
    />
    <!-- Date selection -->
    <DatePicker
      v-if="!noDatepicker"
      v-model="filterStore.dateRange"
      :small="!mdAndLarger"
      :breakpoints="datepickerBreakpoints"
      teleport="#main-layout"
      class="max-w-80 flex-1"
      @breakpoint-limit="onBreakpointLimit"
      @update:model-value="onDateRangeChanged"
    />
    <!-- Mode selection -->
    <template v-if="!noMode">
      <RadioButton
        v-if="xlAndLarger"
        :initial-value="filterStore.mode"
        :items="radioItems"
        class="ml-auto"
        @selection-changed="onModeChanged"
      >
        <template #energy="{ label }">
          <BaseIcon name="flashlight" filled class="mr-1" />
          {{ label }}
        </template>
        <template #finance="{ label }">
          <BaseIcon name="money-euro-circle" filled class="mr-1 text-lg" />
          {{ label }}
        </template>
      </RadioButton>
      <div v-else class="ml-auto">
        <ContextMenu
          :items="radioItems"
          hide-arrow
          plain
          square
          :small="!mdAndLarger"
        >
          <BaseIcon name="eye" filled class="text-xl" />
        </ContextMenu>
      </div>
    </template>
    <!-- Other buttons -->
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import { triggerEvent } from "@/services/plausibleService.js"
import useNavigationStore from "@/stores/navigationStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  BaseIcon,
  DatePicker,
  RadioButton,
  ContextMenu,
} from "@repowerednl/ui-component-library"
import MeteringPointPickerComponent from "@/components/input/MeteringPointPickerComponent.vue"

defineProps({
  noMeteringPointMenu: Boolean,
  noAssets: Boolean,
  noAllocationPoints: Boolean,
  withPortfolio: Boolean,
  uncollapsed: Boolean,
  allocationPoints: {
    type: Array,
    default: null,
  },
  assets: {
    type: Array,
    default: null,
  },
  datepickerBreakpoints: {
    type: Array,
    default: () => [],
  },
  noDatepicker: Boolean,
  noMode: Boolean,
})

const emit = defineEmits(["breakpoint-limit"])

const navigationStore = useNavigationStore()
const filterStore = useFilterStore()
const meteringPointPickerRef = ref()
const breakpoints = useBreakpoints(breakpointsTailwind)
const mdAndLarger = breakpoints.greaterOrEqual("md")
const xlAndLarger = breakpoints.greaterOrEqual("xl")
const highlightPicker = ref(false)

function contextMenuClass(mode) {
  return filterStore.mode === mode ? "font-bold" : ""
}

/**
 * Combines inputs for the radio button (label and value) and the context menu
 * (label, action, classes).
 */
const radioItems = computed(() => [
  {
    label: "Energie",
    value: "energy",
    action: () => onModeChanged("energy"),
    classes: contextMenuClass("energy"),
  },
  {
    label: "Financieel",
    value: "finance",
    action: () => onModeChanged("finance"),
    classes: contextMenuClass("finance"),
  },
  {
    label: "Beide",
    value: "both",
    action: () => onModeChanged("both"),
    classes: contextMenuClass("both"),
  },
])

function onModeChanged(selection) {
  if (selection !== filterStore.mode) {
    filterStore.setMode(selection)
    triggerEvent(`toolbar:mode-${selection}`)
  }
}

function onBreakpointLimit(value) {
  emit("breakpoint-limit", value)
  triggerEvent("toolbar:date-range-breakpoint-limit")
}

const onDateRangeChanged = debounce((newDateRange) => {
  filterStore.setDateRange(newDateRange)
  triggerEvent("toolbar:date-range-changed")
}, DEBOUNCE_DELAY)

/**
 * Open the menu when the 'search' URL query is present
 */
watch(
  [() => navigationStore.doOpenSearch, () => meteringPointPickerRef.value],
  ([doOpenSearch, menuRef]) => {
    if (doOpenSearch && menuRef) {
      menuRef.openMenu(true)
      navigationStore.doOpenSearch = false
    }
  },
  { immediate: true },
)
</script>
